import type { CallRequest, Step, WaitContext } from '../types.action-flow'

/**
 * Resolve the `before` callback (if any) for a step.
 *
 * - `singleTx`: callback lives on the step itself.
 * - `batchTx`: callback lives on the batch's lead call request — the engine
 *   fires it once before the batch's wallet popup.
 * - `signature`: no batch boundary; no callbacks supported.
 */
export function getStepBefore(step: Step): ((ctx: WaitContext) => Promise<void>) | undefined {
  if (step.type === 'singleTx') return step.before
  if (step.type === 'batchTx') return step.callRequests[0]?.before
  return undefined
}

/**
 * Resolve the `after` callback (if any) for a step. Mirror of {@link getStepBefore}
 * — for batches the callback lives on the tail call request.
 */
export function getStepAfter(step: Step): ((ctx: WaitContext) => Promise<void>) | undefined {
  if (step.type === 'singleTx') return step.after
  if (step.type === 'batchTx') return step.callRequests[step.callRequests.length - 1]?.after
  return undefined
}

/**
 * Construction-time double-wait check.
 *
 * Setting `after` on call N and `before` on call N+1 declares the same
 * boundary twice — ambiguous to readers and pays the wait twice. The engine
 * surfaces this as a construction error so the failure shows up at flow-build
 * time, not at the second wait's runtime.
 */
export function assertNoDoubleWait(callRequests: readonly CallRequest[]): void {
  for (const [i, left] of callRequests.entries()) {
    const right = callRequests[i + 1]
    if (right && left.after && right.before) {
      throw new Error(
        `[action-flow] double-wait declared at boundary ${i}→${i + 1}: ` +
          `call ${left.label} has \`after\` and call ${right.label} has \`before\`. ` +
          'Pick one side of the boundary — both sides pay the wait twice.'
      )
    }
  }
}
